import { useState, useEffect, useCallback } from 'react';
import { safeGetJSON, safeSetJSON } from '@/lib/safeStorage';
import { rooms } from '@/data/rooms';

export interface DailyChallenge {
  id: string;
  title: string;
  description: string;
  type: 'visit' | 'complete' | 'time' | 'category' | 'streak';
  target: number;
  current: number;
  completed: boolean;
  reward: string;
  roomId?: string;
  category?: string;
}

interface DailyChallengesState {
  challenges: DailyChallenge[];
  lastRefresh: string;
  totalCompleted: number;
  currentStreak: number;
}

const STORAGE_KEY = 'human-rooms-daily-challenges';

// Challenge templates
const challengeTemplates = [
  // Visit challenges
  { type: 'visit', title: 'Room Explorer', description: 'Visit any 3 rooms today', target: 3, reward: 'Explorer Badge' },
  { type: 'visit', title: 'Curious Mind', description: 'Visit 5 different rooms', target: 5, reward: 'Curious Badge' },
  { type: 'visit', title: 'Deep Diver', description: 'Visit a room you\'ve never been to', target: 1, reward: 'Discovery Badge' },
  
  // Complete challenges
  { type: 'complete', title: 'Truth Seeker', description: 'Complete any room today', target: 1, reward: 'Truth Fragment' },
  { type: 'complete', title: 'Dedicated Soul', description: 'Complete 3 rooms today', target: 3, reward: 'Dedication Badge' },
  
  // Time challenges
  { type: 'time', title: 'Mindful Minutes', description: 'Spend 5 minutes in rooms', target: 300, reward: 'Mindfulness Badge' },
  { type: 'time', title: 'Deep Reflection', description: 'Spend 10 minutes reflecting', target: 600, reward: 'Reflection Badge' },
  
  // Category challenges
  { type: 'category', title: 'Face Your Truth', description: 'Visit a truth room', target: 1, category: 'truth', reward: 'Truth Badge' },
  { type: 'category', title: 'Identity Quest', description: 'Visit an identity room', target: 1, category: 'identity', reward: 'Identity Badge' },
  { type: 'category', title: 'Time Traveler', description: 'Visit a time room', target: 1, category: 'time', reward: 'Time Badge' },
  { type: 'category', title: 'Embrace the Nothing', description: 'Visit a nothing room', target: 1, category: 'nothing', reward: 'Nothing Badge' },
  { type: 'category', title: 'Face Your Fears', description: 'Visit a scary room', target: 1, category: 'scary', reward: 'Courage Badge' },
  { type: 'category', title: 'Daily Ritual', description: 'Check the daily room', target: 1, category: 'daily', reward: 'Ritual Badge' },
  
  // Streak challenges
  { type: 'streak', title: 'Consistency', description: 'Maintain your daily streak', target: 1, reward: 'Streak Badge' },
];

const getTodayKey = () => new Date().toISOString().split('T')[0];

const generateDailyChallenges = (): DailyChallenge[] => {
  // Pick 3 random challenges for today
  const shuffled = [...challengeTemplates].sort(() => Math.random() - 0.5);
  const selected = shuffled.slice(0, 3);
  
  return selected.map((template, index) => ({
    id: `daily-${getTodayKey()}-${index}`,
    title: template.title,
    description: template.description,
    type: template.type as DailyChallenge['type'],
    target: template.target,
    current: 0,
    completed: false,
    reward: template.reward,
    category: template.category,
  }));
};

const defaultState: DailyChallengesState = {
  challenges: [],
  lastRefresh: '',
  totalCompleted: 0,
  currentStreak: 0,
};

export const useDailyChallenges = () => {
  const [state, setState] = useState<DailyChallengesState>(() => {
    const stored = safeGetJSON<DailyChallengesState>(STORAGE_KEY, defaultState);
    const today = getTodayKey();
    
    if (stored.lastRefresh === today && stored.challenges.length > 0) {
      return stored;
    }
    
    // Generate new challenges for today
    return {
      challenges: generateDailyChallenges(),
      lastRefresh: today,
      totalCompleted: stored.totalCompleted || 0,
      currentStreak: stored.currentStreak || 0,
    };
  });

  // Persist state
  useEffect(() => {
    safeSetJSON(STORAGE_KEY, state);
  }, [state]);

  // Check if challenges need refresh
  useEffect(() => {
    const today = getTodayKey();
    if (state.lastRefresh !== today) {
      setState(prev => ({
        challenges: generateDailyChallenges(),
        lastRefresh: today,
        totalCompleted: prev.totalCompleted,
        currentStreak: prev.currentStreak,
      }));
    }
  }, [state.lastRefresh]);

  const updateChallenge = useCallback((type: DailyChallenge['type'], value: number, category?: string) => {
    setState(prev => {
      const updatedChallenges = prev.challenges.map(challenge => {
        if (challenge.completed) return challenge;
        
        let shouldUpdate = challenge.type === type;
        if (type === 'category' && category) {
          shouldUpdate = challenge.type === 'category' && challenge.category === category;
        }
        
        if (shouldUpdate) {
          const newCurrent = challenge.current + value;
          const nowCompleted = newCurrent >= challenge.target;
          return {
            ...challenge,
            current: Math.min(newCurrent, challenge.target),
            completed: nowCompleted,
          };
        }
        return challenge;
      });

      const newCompletedCount = updatedChallenges.filter(c => c.completed).length;
      const prevCompletedCount = prev.challenges.filter(c => c.completed).length;
      
      return {
        ...prev,
        challenges: updatedChallenges,
        totalCompleted: prev.totalCompleted + (newCompletedCount - prevCompletedCount),
      };
    });
  }, []);

  const recordVisit = useCallback((roomId: string) => {
    const room = rooms.find(r => r.id === roomId);
    updateChallenge('visit', 1);
    if (room) {
      updateChallenge('category', 1, room.category);
    }
  }, [updateChallenge]);

  const recordComplete = useCallback((roomId: string) => {
    const room = rooms.find(r => r.id === roomId);
    updateChallenge('complete', 1);
    if (room) {
      updateChallenge('category', 1, room.category);
    }
  }, [updateChallenge]);

  const recordTime = useCallback((seconds: number) => {
    updateChallenge('time', seconds);
  }, [updateChallenge]);

  const getCompletedCount = useCallback(() => {
    return state.challenges.filter(c => c.completed).length;
  }, [state.challenges]);

  return {
    challenges: state.challenges,
    totalCompleted: state.totalCompleted,
    currentStreak: state.currentStreak,
    recordVisit,
    recordComplete,
    recordTime,
    getCompletedCount,
  };
};
